<template>
  <div
    class="modal"
    :class="{ 'is-active': value }">

    <div class="modal-background"
      @click="close()">
    </div>
    
    <div class="modal-content">
      <div class="modal-card">

        <header class="modal-card-head">
          <p class="modal-card-title">Manage Services</p>
          <button
            class="delete"
            aria-label="close"
            @click="close(true)">
          </button>
        </header>

        <div class="modal-card-body">

          <article 
            v-if="!loading && services.length == 0" 
            class="message is-info is-fullwidth mt-3">
            <div class="message-body">
              There are no services.
            </div>
          </article>

          <v-fade-transition mode="out-in">
            <div 
              v-if="loading" 
              class="is-relative">
              <v-spin-loader :value="true">
                <div class="py-6"></div>
              </v-spin-loader>
            </div>

            <table
              v-else 
              class="table is-fullwidth is-hoverable">

              <thead>
                <tr>
                  <th 
                    class="text--nowrap"
                    v-for="(header, headerIndex) in headers" 
                    :key="headerIndex">
                    {{ header.label }}
                  </th>
                  <th></th>
                </tr>
              </thead>

              <tfoot>
                <tr>
                  <td :colspan="headers.length + 1">
                    <div class="centered">
                      <button
                        v-if="!adding"
                        @click="adding = true" 
                        class="button is-primary is-small">
                        Add a new service
                      </button>
                    </div>
                  </td>
                </tr>
              </tfoot>

              <tbody>
                <tr 
                  class="v-centered"
                  v-for="service in services" 
                  :key="service.id">

                  <template v-if="editingId != service.id">
                    <td>
                      {{ service.service_name }}
                    </td>
                    <!-- <td>
                      {{ service.service_type }}
                    </td> -->
                    <td>
                      {{ service.unit ? service.unit.unit_display_name : '' }}
                    </td>
                    <td>
                      {{ service.unit_price || currency(2) }}
                    </td>
                  </template>

                  <template v-else>
                    <td>
                      <v-text-field 
                        required
                        placeholder="Enter Service Name"
                        v-model="service.service_name">
                      </v-text-field>
                    </td>
                    <!-- <td>
                      <v-text-field 
                        placeholder="Enter Service Type"
                        v-model="service.service_type">
                      </v-text-field>
                    </td> -->
                    <td>
                      <v-select
                        required
                        :options="units"
                        item-text="unit_display_name"
                        item-value="id"
                        v-model="service.unit_id"
                      />
                    </td>
                    <td>
                      <v-text-field 
                        placeholder="Enter Service Unit Price"
                        type="number"
                        v-model="service.unit_price"
                      />
                    </td>
                  </template>
                  <td>
                    <div class="buttons buttons-right">
                      <template v-if="editingId != service.id">
                        <button
                          @click="editingId = service.id" 
                          class="button is-small">
                          <span class="icon">
                            <i class="fas fa-edit has-text-grey"></i>
                          </span>
                        </button>
                        <button
                          @click="launchConfirmDelete(service.id)" 
                          class="button is-small">
                          <span class="icon">
                            <i class="fas fa-trash has-text-grey"></i>
                          </span>
                        </button>
                      </template>
                      <template v-else>
                        <button
                          @click="update(service)" 
                          class="button is-primary is-small">
                          <span class="icon">
                            <i class="fas fa-save"></i>
                          </span>
                        </button>
                        <button
                          @click="editingId = null" 
                          class="button is-small">
                          <span class="icon">
                            <i class="fas fa-times has-text-grey"></i>
                          </span>
                        </button>
                      </template>
                    </div>
                  </td>
                </tr>
                <tr class="v-centered" v-if="adding">
                  <td>
                    <v-text-field 
                      required
                      placeholder="Enter Service Name"
                      v-model="service.service_name">
                    </v-text-field>
                  </td>
                  <!-- <td>
                    <v-text-field 
                      placeholder="Enter Service Type"
                      v-model="service.service_type">
                    </v-text-field>
                  </td> -->
                  <td>
                    <v-select
                      required
                      :options="units"
                      item-text="unit_display_name"
                      item-value="id"
                      v-model="service.unit_id"
                    />
                  </td>
                  <td>
                    <v-text-field 
                      placeholder="Enter Service Unit Price"
                      type="number"
                      v-model="service.unit_price"
                    />
                  </td>
                  <td>
                    <div class="buttons buttons-right">
                      <button
                        @click="add" 
                        class="button is-primary is-small">
                        <span class="icon">
                          <i class="fas fa-save"></i>
                        </span>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </v-fade-transition>

        </div>

        <v-confirm-modal 
          v-model="confirmDelete.state"
          :title="confirmDelete.title"
          :message="confirmDelete.message"
          :type="confirmDelete.type"
          @confirm="deleteConfirmedCb ? deleteConfirmedCb() : null"
          @cancel="confirmDelete.state = false"/>

      </div>
    </div>
  </div>
</template>

<script>
import VSelect from '@/components/base/VSelect'
import VTextField from '@/components/base/VTextField'
import VSpinLoader from '@/components/base/VSpinLoader'
import VConfirmModal from '@/components/base/VConfirmModal'
import VFadeTransition from '@/components/transitions/VFadeTransition'

import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  props: {
    value: {
      type: Boolean
    }
  },
  components: { 
    VTextField, VConfirmModal, VSelect, VSpinLoader, VFadeTransition 
  },
  data() {
    return {
      headers: [
        { label: 'Name' },
        { label: 'Unit Type' },
        { label: 'Unit Price' } 
      ],
      service: {
        service_name: '',
        service_type_id: null,
        unit_id: '',
        unit_price: ''
      },
      confirmDelete: {
        state: false,
        title: 'Delete Confirmation',
        message: 'Are you sure you want to delete this service?',
        type: 'danger'
      },
      deleteConfirmedCb: null,
      valid: false,
      adding: false,
      editingId: null
    }
  },
  computed: {
    ...mapState("services", [
      "services", "loading"
    ]),
    ...mapState("units", ["units"])
  },
  watch: {
    value(v) {
      if (!v) {
        this.adding = false;
      }
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("units", ["getUnits"]),
    ...mapActions("services", [
      "getServices","addService", "deleteService", "updateService",
      "toggleAllSelected"
    ]),
    ...mapMutations("services", [
      "selectAll", "deselectAll", "toggleSelected"
    ]),
    initialize() {
      if (this.services.length == 0) {
        this.getServices();
      }
      if (this.units.length == 0) {
        this.getUnits();
      }
    },
    add() {
      this.addService(this.service).then(() => {
        this.adding = false;
        this.clearData();
      });
    },
    update(service) {
      let data = {};
      Object.keys(this.service).forEach(field => {
        data[field] = service[field]
      });

      this.updateService(
        { serviceId: service.id, data: data }
      ).then(() => {
        this.editingId = null;
      })
    },
    launchConfirmDelete(id) {
      this.deleteConfirmedCb = () => {
        this.deleteService(id);
        this.confirmDelete.state = false;
      }
      this.confirmDelete.message = 'Are you sure you want to delete this service?';
      this.confirmDelete.state = true;
    },
    close(clear=false) {
      this.$emit('input', false);
      if (clear) {
        this.clearData();
      }
    },
    clearData() {
      Object.assign(this.$data, this.$options.data.call(this));
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';

.table tfoot td .centered {
  display: flex;
  flex-direction: row;
  justify-content: center;
}

.modal-card {
  border-radius: 6px;
}
</style>
